/*
 * hero-sequence.js — scroll-driven image sequence for the hero background.
 * Renders 48 frames to a <canvas> (cover-fit) and scrubs them as the hero
 * scrolls past (no pinning — scroll stays normal; it's ambient). Also toggles
 * a solid state on the (otherwise transparent) homepage header once the hero
 * has scrolled away. Falls back to a static first frame under reduced motion.
 * Vanilla JS (Dawn style).
 */
(function () {
  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function initSequence(root) {
    if (root.dataset.heroSeqInit === 'true') return;
    var canvas = root.querySelector('[data-hero-seq-canvas]');
    var dataEl = root.querySelector('[data-hero-seq-frames]');
    if (!canvas || !dataEl) return;

    var frames;
    try {
      frames = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!frames || !frames.length) return;
    root.dataset.heroSeqInit = 'true';

    var ctx = canvas.getContext('2d');
    var images = new Array(frames.length);
    var state = { frame: 0 };
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    function draw(i) {
      var idx = Math.max(0, Math.min(frames.length - 1, Math.round(i)));
      var img = images[idx];
      if (!img || !img.complete || !img.naturalWidth) return;
      var cw = canvas.width;
      var ch = canvas.height;
      var scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      var w = img.naturalWidth * scale;
      var h = img.naturalHeight * scale;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }

    function resize() {
      var rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      draw(state.frame);
    }

    frames.forEach(function (src, i) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        if (Math.round(state.frame) === i) draw(state.frame);
      };
      img.src = src;
      images[i] = img;
    });

    resize();
    if (window.ResizeObserver) {
      new ResizeObserver(debounce(resize, 120)).observe(root);
    } else {
      window.addEventListener('resize', debounce(resize, 150));
    }

    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scrub the frames across the hero's natural scroll-through. No pin.
    if (!reduce) {
      gsap.to(state, {
        frame: frames.length - 1,
        ease: 'none',
        onUpdate: function () {
          draw(state.frame);
        },
        scrollTrigger: {
          trigger: root,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.5,
          invalidateOnRefresh: true,
          onRefresh: resize,
        },
      });
    }

    // Transparent homepage header turns solid once the hero scrolls away.
    if (document.body.classList.contains('template-index')) {
      var headerWrap = document.querySelector('.header-group-wrapper');
      if (headerWrap) {
        ScrollTrigger.create({
          trigger: root,
          start: 'top+=100 top',
          onEnter: function () {
            headerWrap.classList.add('header-solid');
          },
          onLeaveBack: function () {
            headerWrap.classList.remove('header-solid');
          },
        });
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-hero-seq]').forEach(initSequence);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-hero-seq]');
    if (root) initSequence(root);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();
